import { AliasResolutionResult } from './status-main-page.models';

/**
 * Returns status route parts from url from the current state.
 */
export function getStatusRoutePartsFromUrl(url: string): [string, string] {
  if (!url) {
    return ['', ''];
  }
  const [pathOnly] = url.split('?');
  const segments = pathOnly.split('/').filter(Boolean);
  const statusMainPageIndex = segments.indexOf('status-main-page');
  if (statusMainPageIndex <= 0) {
    return ['', ''];
  }
  const subdomain = segments[statusMainPageIndex - 1] || '';
  const slug = segments[statusMainPageIndex + 1] || '';
  return [subdomain, slug];
}

/**
 * Checks whether a hostname belongs to the application host (not a public status alias host).
 */
export function isAppHost(hostname?: string): boolean {
  const host = String(hostname || '').toLowerCase().trim();
  if (!host) {
    return false;
  }
  return host === 'localhost' || host.endsWith('.localhost') || host.startsWith('app.');
}

/**
 * Resolves domain name from host using fallback rules.
 */
export function resolveDomainNameFromHost(hostname?: string): string {
  const host = String(hostname || '').toLowerCase().replace(/^www\./, '').trim();
  if (!host) {
    return 'Status';
  }
  if (host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return host;
  }
  const parts = host.split('.').filter(Boolean);
  if (!parts.length) {
    return host;
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return parts.at(-2) || host;
}

/**
 * Returns alias candidates from host from the current state.
 */
export function getAliasCandidatesFromHost(hostname?: string): string[] {
  const host = String(hostname || '')
    .toLowerCase()
    .replace(/^www\./, '')
    .trim();

  if (!host || isAppHost(host)) {
    return [];
  }

  return [host];
}

/**
 * Extracts org slug from status page url from API payloads.
 */
export function extractOrgSlugFromStatusPageUrl(statusPageUrl: any): AliasResolutionResult {
  const value = String(statusPageUrl || '').trim();
  if (!value) {
    return { org: '', slug: '' };
  }

  const match = /\/public\/status_pages\/([^/?#]+)\/([^/?#]+)/i.exec(value);
  if (!match) {
    return { org: '', slug: '' };
  }

  try {
    return {
      org: decodeURIComponent(match[1] || '').trim(),
      slug: decodeURIComponent(match[2] || '').trim()
    };
  } catch {
    return {
      org: String(match[1] || '').trim(),
      slug: String(match[2] || '').trim()
    };
  }
}

/**
 * Extracts org slug from alias response from API payloads.
 */
export function extractOrgSlugFromAliasResponse(response: any): AliasResolutionResult {
  let org = String(
    response?.organizationSlug ||
      response?.organization_slug ||
      response?.org ||
      response?.organization ||
      response?.subdomain ||
      response?.account ||
      ''
  ).trim();

  let slug = String(response?.pageSlug || response?.page_slug || response?.slug || '').trim();

  if (!org || !slug) {
    const fromStatusUrl = extractOrgSlugFromStatusPageUrl(response?.statusPageUrl || response?.status_page_url);
    org = org || fromStatusUrl.org;
    slug = slug || fromStatusUrl.slug;
  }

  return { org, slug };
}
